// OBT F3-C — Kampanya raporu: filtre + tablo + CSV + sayfalama + detay.
// YALNIZ backend'in desteklediği filtreler sunulur (topic/status/park/only_late).
// Tarih/bölge/BM sunucu-tarafı filtresi backend'de OLMADIĞINDAN sunulmaz.
import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download, Filter, ChevronLeft, ChevronRight, BarChart2 } from 'lucide-react';
import { ObtLoading, ObtErrorState, ObtEmptyState, ObtStatusBadge } from '../ObtStates';
import { CAMPAIGN_STATUS_LABELS } from '../obtEnums';
import { useCampaignReport } from './hooks/useCampaignReport';
import CampaignAnalyticsPanel from './CampaignAnalyticsPanel';
import { responseRate, pendingParks } from './reportMetrics';
import { formatPercent, formatDate, toCsv, downloadCsv } from './reportFormat';

const STATUS_OPTIONS = [
  ['', 'Tüm durumlar'],
  ['collecting_responses', 'Cevap Toplanıyor'],
  ['under_review', 'İncelemede'],
  ['director_review', 'Müdür İncelemesinde'],
  ['decided', 'Karara Bağlandı'],
  ['completed', 'Tamamlandı'],
  ['postponed', 'Ertelendi'],
  ['cancelled', 'İptal Edildi'],
  ['draft', 'Taslak'],
];

function buildCsv(items) {
  const headers = [
    'Bilgi Talebi', 'Konu', 'Durum', 'Hedef kişi', 'Park', 'Gönderilen cevap',
    'Cevapsız kişi', 'Süresi geçen kişi', 'Müdür incelemesinde', 'Cevap oranı',
    'Yayın tarihi', 'Son tarih',
  ];
  const rows = items.map((c) => [
    c.title, c.topic, CAMPAIGN_STATUS_LABELS[c.status] || c.status,
    c.target_count, c.park_count, c.submitted_response_count,
    c.no_response_person_count, c.late_person_count, c.pending_director_count,
    formatPercent(responseRate(c)), formatDate(c.published_at), formatDate(c.response_due_at),
  ]);
  return toCsv(headers, rows);
}

export default function CampaignsPanel({ enabled = true }) {
  const rep = useCampaignReport({ enabled });
  const [selected, setSelected] = useState(null);
  const [topicInput, setTopicInput] = useState('');
  const [parkInput, setParkInput] = useState('');

  const { items, page, loading, error, filters, pageSize, offset } = rep;
  const total = page?.total ?? items.length;
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + items.length, total);

  const csvDisabled = items.length === 0;
  const handleExport = () => {
    downloadCsv(`obt-bilgi-talebi-raporu.csv`, buildCsv(items));
  };

  const applyText = () => rep.applyFilters({ topic: topicInput.trim(), parkId: parkInput.trim() });

  const statusValue = filters.status || '';

  const detail = useMemo(() => selected, [selected]);

  return (
    <div className="space-y-4">
      {/* Filtreler */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
            <Filter className="h-4 w-4 text-gray-500" aria-hidden="true" /> Filtreler
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1">
              <Label htmlFor="rep-status">Durum</Label>
              <select
                id="rep-status"
                className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
                value={statusValue}
                onChange={(e) => rep.applyFilters({ status: e.target.value })}
              >
                {STATUS_OPTIONS.map(([v, l]) => (
                  <option key={v || 'all'} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="rep-topic">Konu</Label>
              <Input
                id="rep-topic"
                value={topicInput}
                placeholder="Konu (ör. spor)"
                onChange={(e) => setTopicInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyText()}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="rep-park">Park ID</Label>
              <Input
                id="rep-park"
                value={parkInput}
                placeholder="Park kimliği (opsiyonel)"
                onChange={(e) => setParkInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyText()}
              />
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={Boolean(filters.onlyLate)}
                  onChange={(e) => rep.applyFilters({ onlyLate: e.target.checked })}
                />
                Yalnız süresi geçenler
              </label>
              <Button size="sm" variant="outline" onClick={applyText}>Uygula</Button>
            </div>
          </div>
          <p className="text-xs text-gray-400">
            Tarih / bölge / BM bazlı sunucu-tarafı filtre backend'de tanımlı değildir; bu
            filtreler bilinçli olarak sunulmaz (sahte istemci filtresi uygulanmaz).
          </p>
        </CardContent>
      </Card>

      {/* Liste + CSV */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-gray-500">
          {total > 0 ? `${pageStart}–${pageEnd} / ${total} bilgi talebi` : ''}
        </p>
        <Button size="sm" variant="outline" onClick={handleExport} disabled={csvDisabled}>
          <Download className="mr-2 h-4 w-4" aria-hidden="true" /> CSV indir
        </Button>
      </div>

      {loading && <ObtLoading label="Bilgi Talepleri yükleniyor…" />}
      {!loading && error && <ObtErrorState error={error} onRetry={rep.reload} />}
      {!loading && !error && items.length === 0 && (
        <ObtEmptyState title="Bilgi talebi bulunamadı" description="Filtreleri değiştirip tekrar deneyin." />
      )}

      {!loading && !error && items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2">Bilgi Talebi</th>
                <th className="px-3 py-2">Durum</th>
                <th className="px-3 py-2">Hedef</th>
                <th className="px-3 py-2">Park</th>
                <th className="px-3 py-2">Gönderilen</th>
                <th className="px-3 py-2">Cevapsız</th>
                <th className="px-3 py-2">Süresi geçen</th>
                <th className="px-3 py-2">Cevap oranı</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={String(c.campaign_id)} className="border-t border-gray-100">
                  <td className="px-3 py-2 font-medium text-gray-900">{c.title || 'Bilgi Talebi'}</td>
                  <td className="px-3 py-2">
                    <ObtStatusBadge value={c.status} labelMap={CAMPAIGN_STATUS_LABELS} />
                  </td>
                  <td className="px-3 py-2 text-gray-700">{c.target_count ?? 0}</td>
                  <td className="px-3 py-2 text-gray-700">{c.park_count ?? 0}</td>
                  <td className="px-3 py-2 text-gray-700">{c.submitted_response_count ?? 0}</td>
                  <td className="px-3 py-2 text-gray-700">{c.no_response_person_count ?? 0}</td>
                  <td className="px-3 py-2 text-gray-700">{c.late_person_count ?? 0}</td>
                  <td className="px-3 py-2 text-gray-700">{formatPercent(responseRate(c))}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setSelected(c)}>
                      <BarChart2 className="mr-1 h-4 w-4" aria-hidden="true" /> Detay
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sayfalama */}
      {total > pageSize && (
        <div className="flex items-center justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={offset <= 0 || loading}
            onClick={() => rep.setOffset(Math.max(0, offset - pageSize))}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Önceki
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!page?.has_more || loading}
            onClick={() => rep.setOffset(offset + pageSize)}
          >
            Sonraki <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      )}

      {detail && (
        <CampaignAnalyticsPanel
          campaign={detail}
          enabled={enabled}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
